<script setup lang="ts">
import MarkdownIt from 'markdown-it';
import type { EventResponse } from '@@/types/api';
import { formatEventDateTime } from '~/composables/format-datetime';
import { formatDateRange } from '~/composables/format-date-range';

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const props = defineProps<{
  event: EventResponse;
}>();

const renderedDescription = computed(() =>
  md.render(props.event.description ?? ''),
);

const dateRange = computed(() =>
  formatDateRange(
    props.event.start_datetime ?? '',
    props.event.end_datetime ?? '',
    props.event.timezone ?? '',
  ),
);

const startDateTime = computed(() =>
  formatEventDateTime(
    props.event.start_datetime ?? '',
    props.event.timezone ?? '',
  ),
);

const endDateTime = computed(() =>
  formatEventDateTime(
    props.event.end_datetime ?? '',
    props.event.timezone ?? '',
  ),
);

const DEADLINE_STATUSES = new Set(['Fundraising', 'Confirmed', 'In Progress']);

const fundraisingDeadline = computed(() => {
  if (props.event.user_facing_status !== 'Fundraising') return null;
  const dt = props.event.fundraising_deadline;
  if (!dt || !props.event.timezone) return null;
  return formatEventDateTime(dt, props.event.timezone).formatted;
});

const ticketSalesClose = computed(() => {
  if (!DEADLINE_STATUSES.has(props.event.user_facing_status)) return null;
  const dt = props.event.effective_ticket_sales_close_datetime;
  if (!dt || !props.event.timezone) return null;
  return formatEventDateTime(dt, props.event.timezone).formatted;
});

const hasKeyDates = computed(
  () => fundraisingDeadline.value !== null || ticketSalesClose.value !== null,
);
</script>

<template>
  <div class="event-details-section mb-6">
    <h2 class="text-h6 mb-3">About this event</h2>

    <!-- eslint-disable vue/no-v-html — markdown-it with html:false; no raw user HTML rendered -->
    <div
      class="event-description text-body-1 mb-4"
      v-html="renderedDescription"
    />
    <!-- eslint-enable vue/no-v-html -->

    <div class="d-flex align-start mb-3">
      <v-icon class="mr-3 mt-1" color="primary">mdi-calendar</v-icon>
      <div>
        <div class="text-body-1 font-weight-medium">{{ dateRange }}</div>
        <div class="text-body-2 text-medium-emphasis">
          {{ startDateTime.formatted }} &ndash;
          {{ endDateTime.formatted }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          Event timezone: {{ event.timezone }}
        </div>
      </div>
    </div>

    <div v-if="hasKeyDates" class="d-flex align-start mb-3">
      <v-icon class="mr-3 mt-1" color="primary">mdi-calendar-clock</v-icon>
      <div>
        <div v-if="fundraisingDeadline" class="text-body-2">
          <span class="font-weight-medium">Fundraising ends</span>
          {{ fundraisingDeadline }}
        </div>
        <div v-if="ticketSalesClose" class="text-body-2">
          <span class="font-weight-medium">Ticket sales end</span>
          {{ ticketSalesClose }}
        </div>
      </div>
    </div>

    <div class="d-flex align-start mb-3">
      <v-icon class="mr-3 mt-1" color="primary">mdi-map-marker</v-icon>
      <div>
        <div class="text-body-1 font-weight-medium">
          {{ event.location_name ?? event.location_address ?? '' }}
        </div>
        <div
          v-if="event.location_address"
          class="text-body-2 text-medium-emphasis"
        >
          {{ event.location_address }}
        </div>
      </div>
    </div>

    <div class="d-flex align-start">
      <v-icon class="mr-3 mt-1" color="primary">mdi-account</v-icon>
      <div>
        <div class="text-body-1 font-weight-medium">
          Organized by {{ event.organizer?.name ?? 'Event Organizer' }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.event-description :deep(h1),
.event-description :deep(h2),
.event-description :deep(h3) {
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.event-description :deep(ul),
.event-description :deep(ol) {
  padding-left: 1.5em;
  margin-bottom: 1em;
}

.event-description :deep(a) {
  color: rgb(var(--v-theme-primary));
}

.event-description :deep(code) {
  background: rgb(var(--v-theme-surface-variant));
  padding: 0.15em 0.4em;
  border-radius: 4px;
  font-size: 0.9em;
}

.event-description :deep(pre) {
  background: rgb(var(--v-theme-surface-variant));
  padding: 1em;
  border-radius: 8px;
  overflow-x: auto;
  margin-bottom: 1em;
}

.event-description :deep(blockquote) {
  border-left: 3px solid rgb(var(--v-theme-outline));
  padding-left: 1em;
  margin-left: 0;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.event-description :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
